import { useContext, useEffect, useState } from "react";
import ProjectInfo from "../../models/projectInfo";
import TableColum from "../../models/tableColumn";
import { Chart } from "../../models/chart";
import User from "../../models/user";
import UserContext from "../../contexts/userContext";
import ValidatorManager from "../../components/common/validationManager";
import ModalWindow from "../common/overlays/ModalWindow";
import Validator from "../common/Validator";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ColorPicker from "../common/ColorPicker";
import nameOf from "../../util/nameOfHelper";
import colorHelper from "../../util/colorHelper";
import ChartSeries from "../../models/chartSeries";
import Dropdown from "../common/Dropdown";
import DropdownOption from "../common/DropdownOption";
import TableColumnDataType from "../../models/tableColumnDataType";
import ChartSeriesType from "../../models/chartSeriesType";

const validator = {
  chartName: "chartName",
  xAxisName: "xAxisName",
  yAxisName: "yAxisName",
};

const ChartsEdit: React.FC<{
  project: ProjectInfo;
  tableColumns: TableColum[];
  chart: Chart;
  isNew: boolean;
  onCreateChart: any;
  onUpdateChart: any;
  onDeleteChart: any;
  onCancel: any;
}> = ({
  project,
  tableColumns,
  chart: inputChart,
  isNew,
  onCreateChart,
  onUpdateChart,
  onDeleteChart,
  onCancel,
}) => {
  const [showDeleteWindow, setShowDeleteWindow] = useState<boolean>(false);

  const [showConfirmIsShared, setShowConfirmIsShared] =
    useState<Boolean>(false);

  const [showConfirmIsDefault, setShowConfirmIsDefault] =
    useState<Boolean>(false);

  const [showConfirmSave, setShowConfirmSave] = useState<Boolean>(false);

  const user = useContext<User | undefined>(UserContext);

  const [validatorManager] = useState<ValidatorManager>(new ValidatorManager());

  const [errorMessage, setErrorMessage] = useState<string>("");

  const [chart, setChart] = useState<Chart>(new Chart());

  const [seriesTypeOptions, setSeriesTypeOptions] = useState<
    DropdownOption<string>[]
  >([]);

  const [fieldOptions, setFieldOptions] = useState<DropdownOption<string>[]>(
    [],
  );

  const [allFieldOptions, setAllFieldOptions] = useState<
    DropdownOption<string>[]
  >([]);

  const setTypeAndFieldOptionsForChartSeries = (
    chartSeries: ChartSeries[],
    seriesTypeOptions: DropdownOption<string>[],
    fieldOptions: DropdownOption<string>[],
  ) => {
    for (const series of chartSeries) {
      series.selectedSeriesTypeOption = seriesTypeOptions.find(
        (to) => to.value === series.seriesType,
      );

      series.selectedFieldOption = fieldOptions.find(
        (fo) => fo.value === series.fieldName,
      );
    }
  };

  const setXAxisFieldOption = (
    chart: Chart,
    fieldOptions: DropdownOption<string>[],
  ) => {
    chart.selectedXAxisFieldOption = fieldOptions.find(
      (fo) => fo.value === chart.xAxisFieldName,
    );

    if (!chart.selectedXAxisFieldOption) {
      chart.selectedXAxisFieldOption = fieldOptions[0];
      if (fieldOptions[0].value) {
        chart.xAxisFieldName = fieldOptions[0].value;
      }
    }
  };

  useEffect(() => {
    const seriesTypeOptions: DropdownOption<string>[] = [
      { value: ChartSeriesType.Area, label: ChartSeriesType.Area },
      { value: ChartSeriesType.Bar, label: ChartSeriesType.Bar },
      { value: ChartSeriesType.Line, label: ChartSeriesType.Line },
      { value: ChartSeriesType.Point, label: ChartSeriesType.Point },
    ];
    setSeriesTypeOptions(seriesTypeOptions);

    const fieldOptions: DropdownOption<string>[] = tableColumns
      .filter((c) => c.dataType === TableColumnDataType.Number)
      .map((c) => {
        return { value: c.columnName, label: c.displayName };
      });
    setFieldOptions(fieldOptions);

    const allFieldOptions: DropdownOption<string>[] = tableColumns.map((c) => {
      return { value: c.columnName, label: c.displayName };
    });
    setAllFieldOptions(allFieldOptions);

    if (!isNew) {
      validatorManager.setIsValid(validator.chartName, true);
      validatorManager.setIsValid(validator.xAxisName, true);
      validatorManager.setIsValid(validator.yAxisName, true);

      setTypeAndFieldOptionsForChartSeries(
        inputChart.chartSeries,
        seriesTypeOptions,
        fieldOptions,
      );
    }

    setXAxisFieldOption(inputChart, allFieldOptions);

    setChart(inputChart);
  }, [project, tableColumns, inputChart, isNew, validatorManager]);

  const onChartNameChanged = (event: any) => {
    const newValue = event.target.value;

    const newChart = { ...chart, chartName: newValue };
    setChart(newChart);

    const isValid = newValue.length !== 0;
    validatorManager.setIsValid(validator.chartName, isValid);
  };

  const onXAxisNameChanged = (event: any) => {
    const newValue = event.target.value;

    const newChart = { ...chart, xAxisName: newValue };
    setChart(newChart);

    const isValid = newValue.length !== 0;
    validatorManager.setIsValid(validator.xAxisName, isValid);
  };

  const onYAxisNameChanged = (event: any) => {
    const newValue = event.target.value;

    const newChart = { ...chart, yAxisName: newValue };
    setChart(newChart);

    const isValid = newValue.length !== 0;
    validatorManager.setIsValid(validator.yAxisName, isValid);
  };

  const onSave = async () => {
    setErrorMessage("");

    if (!validatorManager.validate()) {
      console.log("return");
      return;
    }

    try {
      if (isNew) {
        await onCreateChart(chart);
      } else {
        await onUpdateChart(chart, inputChart);
      }
    } catch (error: any) {
      const errorMessage = error.response.data;
      setErrorMessage(errorMessage);
      return;
    }
  };

  const onDeleteConfirm = () => {
    setShowDeleteWindow(true);
  };

  const onDeleteOk = async () => {
    setShowDeleteWindow(false);

    onDeleteChart(chart);
  };

  const onDeleteCancel = () => {
    setShowDeleteWindow(false);
  };

  // --------- IsShared

  const onIsSharedChanged = (event: any) => {
    const checked = event.target.checked;

    if (checked) {
      onConfirmIsShared();
      return;
    }

    const newChart = { ...chart, isShared: false };
    setChart(newChart);
  };

  const onConfirmIsShared = () => {
    setShowConfirmIsShared(true);
  };

  const onOkIsShared = () => {
    setShowConfirmIsShared(false);

    const newChart = { ...chart, isShared: true };
    setChart(newChart);
  };

  const onCancelIsShared = () => {
    setShowConfirmIsShared(false);
  };

  /////////////////////

  // --------- IsDefault

  const onIsDefaultChanged = (event: any) => {
    const checked = event.target.checked;

    if (checked) {
      onConfirmIsDefault();
      return;
    }

    const newChart = { ...chart, isDefault: false, isShared: false };
    setChart(newChart);
  };

  const onConfirmIsDefault = () => {
    setShowConfirmIsDefault(true);
  };

  const onOkIsDefault = () => {
    setShowConfirmIsDefault(false);

    const newChart = { ...chart, isDefault: true, isShared: true };
    setChart(newChart);
  };

  const onCancelIsDefault = () => {
    setShowConfirmIsDefault(false);
  };

  /////////////////////

  // --------- Save

  const onConfirmSave = () => {
    if (chart.isShared || chart.isDefault) {
      setShowConfirmSave(true);
    } else {
      onOkSave();
    }
  };

  const onOkSave = async () => {
    setShowConfirmSave(false);

    await onSave();
  };

  const onCancelSave = () => {
    setShowConfirmSave(false);
  };

  /////////////////////

  const onResetToDefault = () => {
    if (!chart.parentChart) {
      return;
    }

    const defaultChart = structuredClone(chart.parentChart);

    if (!defaultChart) {
      return;
    }

    defaultChart.isShared = false;
    defaultChart.isDefault = false;
    defaultChart.isSharedDataChanged = false;

    defaultChart.idChart = chart.idChart;
    defaultChart.idUser = chart.idUser;
    defaultChart.idParentChart = chart.idParentChart;
    defaultChart.parentChart = chart.parentChart;

    setTypeAndFieldOptionsForChartSeries(
      defaultChart.chartSeries,
      seriesTypeOptions,
      fieldOptions,
    );

    // mark series as new
    for (const series of defaultChart.chartSeries) {
      series.idChartSeries = 0;
      series.idChart = chart.idChart;
    }

    setXAxisFieldOption(defaultChart, allFieldOptions);

    setChart(defaultChart);
  };

  const getSharedOrDefaultLabel = () => {
    if (chart.isDefault) {
      return "Default";
    }

    if (chart.isShared) {
      return "Shared";
    }

    return "";
  };

  const onSeriesNameChanged = (event: any, series: ChartSeries) => {
    const newValue = event.target.value;
    setNewSeriesList(series, {
      [nameOf<ChartSeries>((p) => p.seriesName)]: newValue,
    });
  };

  const onSeriesTypeOptionChaged = async (
    newValue: DropdownOption<string>,
    series: ChartSeries,
  ) => {
    series.selectedSeriesTypeOption = newValue;

    if (!newValue.value) {
      return;
    }

    setNewSeriesList(series, {
      [nameOf<ChartSeries>((p) => p.seriesType)]: newValue.value,
    });
  };

  const onFieldOptionChaged = async (
    newValue: DropdownOption<string>,
    series: ChartSeries,
  ) => {
    series.selectedFieldOption = newValue;

    if (!newValue.value) {
      return;
    }

    setNewSeriesList(series, {
      [nameOf<ChartSeries>((p) => p.fieldName)]: newValue.value,
    });
  };

  const onSeriesColorChanged = (color: string, series: ChartSeries) => {
    setNewSeriesList(series, {
      [nameOf<ChartSeries>((p) => p.seriesColor)]: color,
    });
  };

  const onSeriesShowColorPickerChanged = (series: ChartSeries) => {
    setNewSeriesList(series, {
      [nameOf<ChartSeries>((p) => p.showColorPicker)]: !series.showColorPicker,
    });
  };

  const onSeriesColorPickerClickOutside = (series: ChartSeries) => {
    setNewSeriesList(series, {
      [nameOf<ChartSeries>((p) => p.showColorPicker)]: false,
    });
  };

  const setNewSeriesList = (series: ChartSeries, props: any) => {
    const newSeriesList = chart.chartSeries.map((r) =>
      r === series ? { ...r, ...props } : { ...r },
    );

    const newChart = { ...chart, chartSeries: newSeriesList };
    setChart(newChart);
  };

  const onNewSeries = () => {
    const newSeries = new ChartSeries();
    newSeries.seriesColor = colorHelper.getRandomColor();

    newSeries.selectedSeriesTypeOption = seriesTypeOptions[0];
    newSeries.seriesType = seriesTypeOptions[0].value ?? "";

    newSeries.selectedFieldOption = fieldOptions[0];
    newSeries.fieldName = fieldOptions[0].value ?? "";

    const indexes: number[] = chart.chartSeries.map((r) => r.index);
    newSeries.index = indexes.length > 0 ? Math.max(...indexes) + 1 : 1;

    const newSeriesList = [...chart.chartSeries];
    newSeriesList.push(newSeries);

    const newChart = { ...chart, chartSeries: newSeriesList };
    setChart(newChart);
  };

  const onDeleteSeries = (series: ChartSeries) => {
    const newSeriesList = chart.chartSeries.filter((s) => s !== series);

    const newChart = { ...chart, chartSeries: newSeriesList };
    setChart(newChart);
  };

  const onUseDistanceOnXAxisChanged = (event: any) => {
    const checked = event.target.checked;

    const newChart = { ...chart, useDistanceOnXAxis: checked };
    setChart(newChart);
  };

  const onSelectedXAxisFieldOptionChaged = async (
    newValue: DropdownOption<string>,
  ) => {
    chart.selectedXAxisFieldOption = newValue;

    if (!newValue.value) {
      return;
    }

    const newChart = { ...chart, xAxisFieldName: newValue.value };
    setChart(newChart);
  };

  return (
    <div style={{ background: "white", padding: "10px", minWidth: "300px" }}>
      <div className="row">
        <div className="mb-3 d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-center">
            <div style={{ textWrap: "nowrap" }}>
              <b>
                {isNew
                  ? "New Chart"
                  : `Edit ${getSharedOrDefaultLabel()} Chart`}
              </b>
            </div>
            {!chart.parentChart && (
              <div className="d-flex align-items-center ms-2">
                <label className="form-check-label" htmlFor="isShared">
                  Share
                </label>
                <div className="form-switch form-switch-right-text">
                  <input
                    className="form-check-input form-switch-right-text"
                    type="checkbox"
                    checked={chart.isShared}
                    onChange={onIsSharedChanged}
                    id="isShared"
                    disabled={chart.isDefault}
                  />
                </div>
                {showConfirmIsShared && (
                  <ModalWindow
                    title="Share Chart"
                    onOk={onOkIsShared}
                    onCancel={onCancelIsShared}
                  >
                    <div>
                      Are you sure you want to share chart to be visible by all
                      users?
                    </div>
                  </ModalWindow>
                )}
              </div>
            )}
          </div>
          {user?.isPortalAdmin && !chart.parentChart && (
            <div className="d-flex align-items-center ms-3">
              <div className="badge bg-secondary me-2">PORTAL ADMIN</div>
              <label
                className="form-check-label"
                style={{ textWrap: "nowrap" }}
                htmlFor="isDefaultChart"
              >
                Project Default Chart
              </label>
              <div className="form-switch form-switch-right-text">
                <input
                  className="form-check-input form-switch-right-text"
                  type="checkbox"
                  checked={chart.isDefault}
                  onChange={onIsDefaultChanged}
                  id="isDefaultChart"
                />
              </div>
              {showConfirmIsDefault && (
                <ModalWindow
                  title="Set Project Default Chart"
                  onOk={onOkIsDefault}
                  onCancel={onCancelIsDefault}
                >
                  <div>
                    Are you sure you want to set the chart as a project default
                    chart to be visible by all users?
                  </div>
                </ModalWindow>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="row">
        <div className="mb-1">
          <label htmlFor="chartName" className="form-label">
            Chart Name
          </label>
          <div className="input-group">
            <Validator
              name={validator.chartName}
              errorMessage="Please enter a Chart name."
              validationManager={validatorManager}
            >
              <input
                type="text"
                className="form-control"
                id="chartName"
                value={chart.chartName}
                onChange={onChartNameChanged}
              />
            </Validator>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="mb-1">
          <label htmlFor="xAxisName" className="form-label">
            X Axis Name
          </label>
          <div className="input-group">
            <Validator
              name={validator.xAxisName}
              errorMessage="Please enter Chart X Axis name."
              validationManager={validatorManager}
            >
              <input
                type="text"
                className="form-control"
                id="xAxisName"
                value={chart.xAxisName}
                onChange={onXAxisNameChanged}
              />
            </Validator>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="mb-1">
          <label htmlFor="yAxisName" className="form-label">
            Y Axis Name
          </label>
          <div className="input-group">
            <Validator
              name={validator.yAxisName}
              errorMessage="Please enter Chart Y Axis name."
              validationManager={validatorManager}
            >
              <input
                type="text"
                className="form-control"
                id="yAxisName"
                value={chart.yAxisName}
                onChange={onYAxisNameChanged}
              />
            </Validator>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="mb-1 mt-1">
          <div className="d-flex align-items-center">
            <label
              className="form-check-label"
              style={{ textWrap: "nowrap" }}
              htmlFor="useDistanceOnXAxis"
            >
              X Axis Use Distance
            </label>
            <div className="form-switch form-switch-right-text">
              <input
                className="form-check-input form-switch-right-text"
                type="checkbox"
                checked={chart.useDistanceOnXAxis}
                onChange={onUseDistanceOnXAxisChanged}
                id="useDistanceOnXAxis"
              />
            </div>
            <label
              className="form-check-label ms-4"
              style={{ textWrap: "nowrap" }}
            >
              X Axis Use Field
            </label>
            <div className="ms-2" style={{ zIndex: 3 }}>
              <Dropdown
                minWidth="200px"
                height="35px"
                options={allFieldOptions}
                value={chart.selectedXAxisFieldOption}
                onChange={(newValue: any) =>
                  onSelectedXAxisFieldOptionChaged(newValue)
                }
                disabled={chart.useDistanceOnXAxis}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="mt-2 mb-2">
          <div className="d-flex align-items-center mb-2">
            <div
              className="image-button-link d-flex align-items-center"
              onClick={onNewSeries}
            >
              <AddIcon />
              <div className="image-button-link-text">Add Series</div>
            </div>
          </div>
          {chart.chartSeries.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th scope="col"></th>
                  <th scope="col">Series Name</th>
                  <th scope="col">Series Type</th>
                  <th scope="col">Field</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {chart.chartSeries.map((series: ChartSeries, index) => (
                  <tr key={index.toString()}>
                    <td>
                      <div style={{ marginTop: "4px", marginRight: "4px" }}>
                        <div
                          style={{
                            padding: "5px",
                            background: "#fff",
                            borderRadius: "1px",
                            boxShadow: "0 0 0 1px rgba(0,0,0,.1)",
                            display: "inline-block",
                            cursor: "pointer",
                          }}
                        >
                          <div
                            style={{
                              width: "20px",
                              height: "20px",
                              background: series.seriesColor,
                            }}
                            onClick={() =>
                              onSeriesShowColorPickerChanged(series)
                            }
                          ></div>
                        </div>
                        {series.showColorPicker && (
                          <ColorPicker
                            color={series.seriesColor}
                            onColorChange={(color: any) =>
                              onSeriesColorChanged(color.hex, series)
                            }
                            onClickOutside={() =>
                              onSeriesColorPickerClickOutside(series)
                            }
                          />
                        )}
                      </div>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        value={series.seriesName}
                        onChange={(event) => onSeriesNameChanged(event, series)}
                      />
                    </td>
                    <td>
                      <Dropdown
                        minWidth="80px"
                        height="35px"
                        options={seriesTypeOptions}
                        value={series.selectedSeriesTypeOption}
                        onChange={(newValue: any) =>
                          onSeriesTypeOptionChaged(newValue, series)
                        }
                      />
                    </td>
                    <td>
                      <Dropdown
                        minWidth="200px"
                        height="35px"
                        options={fieldOptions}
                        value={series.selectedFieldOption}
                        onChange={(newValue: any) =>
                          onFieldOptionChaged(newValue, series)
                        }
                      />
                    </td>
                    <td>
                      <div
                        className="image-button-link"
                        onClick={() => onDeleteSeries(series)}
                      >
                        <ClearIcon />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="d-flex justify-content-between">
        <div>
          <button className="btn btn-sm btn-primary" onClick={onConfirmSave}>
            {isNew ? "Create" : "Save"}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            style={{ marginLeft: "10px" }}
            onClick={() => {
              onCancel();
            }}
          >
            Cancel
          </button>
          {errorMessage !== "" && (
            <div className="error-message col-md-12 text-center mt-3">
              {errorMessage}
            </div>
          )}
        </div>
        {!isNew && !chart.parentChart && (
          <div>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={onDeleteConfirm}
            >
              Delete
            </button>
          </div>
        )}
        {chart.parentChart && (
          <div>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={onResetToDefault}
            >
              Reset to Default
            </button>
          </div>
        )}
      </div>
      {showDeleteWindow && (
        <ModalWindow title="Delete" onOk={onDeleteOk} onCancel={onDeleteCancel}>
          <div>
            {`Are you sure you want to delete the chart: ${chart?.chartName}?`}
          </div>
        </ModalWindow>
      )}
      {showConfirmSave && (
        <ModalWindow
          title={isNew ? "Create" : "Save"}
          onOk={onOkSave}
          onCancel={onCancelSave}
        >
          <div>
            You are {isNew ? "creating" : "editing"} a{" "}
            <span className="text-danger">
              <b>{`${getSharedOrDefaultLabel()} Chart`}</b>
            </span>
            .{" "}
            <b>
              {isNew ? "The chart" : "Changes"} will be visible to all users.
            </b>
            <br />
            Are you sure you want to{" "}
            {isNew ? "create the chart" : "save changes"}?
          </div>
        </ModalWindow>
      )}
    </div>
  );
};

export default ChartsEdit;
